import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import KinAvatar from "@/components/KinAvatar";
import SocialAuthButtons from "@/components/SocialAuthButtons";
import { toast } from "@/hooks/use-toast";
import { useStatusBar } from "@/hooks/useStatusBar";
import { openExternal } from "@/lib/openExternal";
import { NETWORK_ERROR_MESSAGE, isAuthNetworkError, reportAuthNetworkError } from "@/lib/authNetworkError";

const SignUp = () => {
  useStatusBar({ color: 'F0F0FF', style: 'dark' });
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [confirmationSent, setConfirmationSent] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldError(null);

    if (password !== confirmPassword) {
      setFieldError("Passwords don't match");
      return;
    }
    if (password.length < 6) {
      setFieldError("Make it at least 6 characters, mama 💜");
      return;
    }

    setLoading(true);
    const userEmail = email.trim();
    // Name capture moved out of this form -- the post-tutorial NameCaptureModal
    // (Index.tsx) and the existing-user nudge handle writing first_name now.
    const { data, error } = await supabase.auth.signUp({
      email: userEmail,
      password,
      // /auth/callback is a dedicated route so it can be an AASA Universal Link
      // target (bare "/" can't be). See src/pages/AuthCallback.tsx.
      options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
    });

    if (error) {
      // The dropped-connection case gets one plain sentence instead of the raw
      // Safari/Chrome text, which leaks our Supabase hostname. Everything else
      // Supabase says keeps its own wording. See lib/authNetworkError.ts.
      if (isAuthNetworkError(error)) {
        reportAuthNetworkError(error, "signup");
        setError(NETWORK_ERROR_MESSAGE);
      } else {
        setError(error.message);
      }
      // Fields are untouched and loading drops here, so the button she is
      // already looking at is tappable again immediately.
      setLoading(false);
      return;
    }

    // Supabase returns data.user with an empty identities array when the email
    // is already registered (it does this to prevent email enumeration).
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      setError("This email is already registered.");
      setLoading(false);
      return;
    }

    // If session exists, user is signed in (auto-confirm was on)
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      navigate("/");
    } else {
      setConfirmationSent(userEmail);
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!confirmationSent) return;
    setResending(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: confirmationSent,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
    });
    setResending(false);
    if (error) {
      toast({ title: "Couldn't resend", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Email resent 💜", description: "Check your inbox again." });
    }
  };

  // Centered layout that still scrolls: #root is height:100dvh; overflow:hidden,
  // so the scroll container has to live here. Fixed height + overflow-y-auto on
  // the outer div, min-h-full on the inner centering wrapper — content shorter
  // than the viewport stays vertically centered, taller content scrolls instead
  // of clipping from both ends (issue #359).
  if (confirmationSent) {
    return (
      <div className="h-[100dvh] overflow-y-auto" style={{ backgroundColor: "#F0F0FF" }}>
        <div
          className="min-h-full flex flex-col items-center justify-center px-6 py-10"
          style={{ paddingBottom: "calc(2.5rem + env(safe-area-inset-bottom, 0px))" }}
        >
          <div className="w-full max-w-sm flex flex-col items-center text-center">
            <KinAvatar size="lg" variant="dark" />
            <h1 className="font-display mt-5 mb-3" style={{ fontSize: 28, color: "#1C1A3E" }}>
              Check your email.
            </h1>
            <p className="font-body" style={{ fontSize: 14, color: "#54525C", lineHeight: 1.5 }}>
              We sent a confirmation link to{" "}
              <span style={{ color: "#5050C8", fontWeight: 600 }}>{confirmationSent}</span>. Tap the link to verify your account and get started.
            </p>
            <p className="mt-6 font-body" style={{ fontSize: 14, color: "#54525C" }}>
              Didn't get it?{" "}
              <button
                onClick={handleResend}
                disabled={resending}
                className="font-semibold bg-transparent border-none cursor-pointer disabled:opacity-50"
                style={{ color: "#5050C8" }}
              >
                {resending ? "Resending…" : "Resend email"}
              </button>
            </p>
            <Link to="/signin" className="mt-3 font-body" style={{ fontSize: 14, color: "#54525C" }}>
              Back to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] overflow-y-auto" style={{ backgroundColor: "#F0F0FF" }}>
      <div
        className="min-h-full flex flex-col items-center justify-center px-6 py-10"
        style={{ paddingBottom: "calc(2.5rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="w-full max-w-sm flex flex-col items-center">
          <div className="flex-shrink-0">
            <KinAvatar size="lg" variant="dark" />
          </div>

          <h1 className="font-display text-foreground text-center mt-5 mb-1" style={{ fontSize: "28px", color: "#1C1A3E" }}>
            Let's get you set up.
          </h1>
          <p className="font-body text-center" style={{ fontSize: 14, color: "#54525C", lineHeight: 1.45 }}>
            Finally, something that keeps up with your whole mom brain.
          </p>

          <form onSubmit={handleSignUp} className="w-full mt-6 flex flex-col gap-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full h-12 px-4 rounded-xl border font-body text-base bg-white focus:outline-none focus:ring-2 transition-all"
              style={{ borderColor: "#C8C8F4", color: "#1C1A3E" }}
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full h-12 px-4 rounded-xl border font-body text-base bg-white focus:outline-none focus:ring-2 transition-all"
              style={{ borderColor: "#C8C8F4", color: "#1C1A3E" }}
            />
            <div>
              <input
                type="password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full h-12 px-4 rounded-xl border font-body text-base bg-white focus:outline-none focus:ring-2 transition-all"
                style={{ borderColor: "#C8C8F4", color: "#1C1A3E" }}
              />
              {fieldError && (
                <p className="text-sm font-body mt-1" style={{ color: "#B91C1C" }}>{fieldError}</p>
              )}
            </div>

            <div
              onClick={() => setAcceptedTerms((v) => !v)}
              className="flex items-center cursor-pointer select-none"
              style={{ minHeight: 44, gap: 10 }}
            >
              <button
                type="button"
                role="checkbox"
                aria-checked={acceptedTerms}
                onClick={(e) => { e.stopPropagation(); setAcceptedTerms((v) => !v); }}
                className="flex items-center justify-center shrink-0 transition-all"
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 4,
                  border: "1.5px solid #C8C8F4",
                  backgroundColor: acceptedTerms ? "#5050C8" : "transparent",
                }}
              >
                {acceptedTerms && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
              <span className="font-body" style={{ fontSize: 12.5, color: "#54525C", lineHeight: 1.45 }}>
                By continuing, you agree to our{" "}
                <a href="https://kinyoudoitall.com/terms-of-service/" target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); e.preventDefault(); openExternal("https://kinyoudoitall.com/terms-of-service/"); }} className="underline" style={{ color: "#5050C8" }}>
                  Terms
                </a>{" "}
                and{" "}
                <a href="https://kinyoudoitall.com/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); e.preventDefault(); openExternal("https://kinyoudoitall.com/privacy"); }} className="underline" style={{ color: "#5050C8" }}>
                  Privacy Policy
                </a>
                .
              </span>
            </div>

            {error && (
              <div className="text-sm font-body" style={{ color: "#B91C1C" }}>
                <p>{error}</p>
                {error === "This email is already registered." && (
                  <Link
                    to={`/signin?email=${encodeURIComponent(email.trim())}`}
                    className="font-semibold underline mt-1 inline-block"
                    style={{ color: "#5050C8" }}
                  >
                    Sign in instead?
                  </Link>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !acceptedTerms}
              className="w-full h-12 font-body font-semibold text-base text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: "#5050C8", borderRadius: "14px" }}
            >
              {loading ? "Creating account…" : "Create account"}
            </button>
          </form>

          <SocialAuthButtons />

          <p className="mt-4 font-body text-sm" style={{ color: "#54525C" }}>
            Already have an account?{" "}
            <Link to="/signin" className="font-semibold" style={{ color: "#5050C8" }}>
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
